import { articles } from '@/mocks/articles';

export type Lang = 'en' | 'pt' | 'de';

export interface SeoMeta {
  title: string;
  description: string;
  keywords: string;
  canonicalPath: string;
  image: string;
  ogType: 'website' | 'article';
  lang: Lang;
}

// Brand assets traceable to authoritative embed_code favicon and existing project images.
export const SITE_NAME = 'CSSBuy Spreadsheet';
export const SITE_LOGO =
  'https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/d367cf6745ee47f3fc82c76d3a54c50c.png';
export const SITE_IMAGE =
  'https://readdy.ai/api/search-image?query=Abstract%20soft%20neutral%20gradient%20background%20with%20floating%20fashion%20product%20silhouettes%2C%20warm%20cream%20and%20beige%20tones%2C%20minimal%20clean%20aesthetic%2C%20subtle%20texture%2C%20editorial%20style%2C%20gentle%20shadows%2C%20premium%20modern%20design%2C%20suitable%20for%20website%20hero%20section&width=1600&height=900&seq=hero-bg-001&orientation=landscape';

export const DEFAULT_ISO_DATE = '2026-08-27';

export function buildSiteOrigin(): string {
  const configured = import.meta.env.VITE_SITE_URL as string | undefined;
  if (configured && configured.trim()) {
    return configured.trim().replace(/\/+$/, '');
  }
  if (typeof window !== 'undefined' && window.location.origin) {
    return window.location.origin;
  }
  return '';
}

interface LocaleSeo {
  title: string;
  description: string;
  keywords: string;
}

const homeSeo: Record<Lang, LocaleSeo> = {
  en: {
    title: 'CSSBuy Spreadsheet | Independent Resource Hub & Research Guide',
    description:
      'Independent CSSBuy Spreadsheet resource with organized product categories, buying guides, reviews, shipping costs, QC info and safety tips.',
    keywords:
      'CSSBuy Spreadsheet, CSSBuy, spreadsheet, product research, buying guide, shipping cost, QC, warehouse',
  },
  pt: {
    title: 'CSSBuy Spreadsheet | Hub de Recursos Independente e Guia de Pesquisa',
    description:
      'Recurso independente de CSSBuy Spreadsheet com categorias de produtos organizadas, guias de compra, reviews, custos de envio, informações de QC e dicas de segurança.',
    keywords:
      'CSSBuy Spreadsheet, CSSBuy, planilha, pesquisa de produtos, guia de compra, custo de envio, QC, armazém',
  },
  de: {
    title: 'CSSBuy Spreadsheet | Unabhängiger Ressourcen-Hub & Recherche-Guide',
    description:
      'Unabhängige CSSBuy Spreadsheet-Ressource mit organisierten Produktkategorien, Kaufguides, Reviews, Versandkosten, QC-Infos und Sicherheitstipps.',
    keywords:
      'CSSBuy Spreadsheet, CSSBuy, Tabelle, Produktrecherche, Kaufguide, Versandkosten, QC, Lager',
  },
};

const legalSeo: Record<string, Record<Lang, LocaleSeo>> = {
  about: {
    en: {
      title: 'About Us | CSSBuy Spreadsheet Independent Resource',
      description:
        'Learn about our independent CSSBuy Spreadsheet resource hub. We provide organized product research guides, categories, and buying resources.',
      keywords: 'CSSBuy Spreadsheet, CSSBuy, about, independent resource, product research',
    },
    pt: {
      title: 'Sobre Nós | CSSBuy Spreadsheet Recurso Independente',
      description:
        'Conheça nosso hub de recursos independente CSSBuy Spreadsheet. Fornecemos guias de pesquisa de produtos organizados, categorias e recursos de compra.',
      keywords: 'CSSBuy Spreadsheet, CSSBuy, sobre, recurso independente, pesquisa de produtos',
    },
    de: {
      title: 'Über uns | CSSBuy Spreadsheet Unabhängige Ressource',
      description:
        'Erfahren Sie mehr über unseren unabhängigen CSSBuy Spreadsheet Ressourcen-Hub. Wir bieten organisierte Produktrecherche-Guides, Kategorien und Kaufressourcen.',
      keywords: 'CSSBuy Spreadsheet, CSSBuy, über uns, unabhängige Ressource, Produktrecherche',
    },
  },
  disclaimer: {
    en: {
      title: 'Disclaimer | CSSBuy Spreadsheet Independent Resource',
      description:
        'Read the disclaimer for CSSBuy Spreadsheet, an independent informational resource not affiliated with or operated by CSSBuy.',
      keywords: 'CSSBuy Spreadsheet, CSSBuy, disclaimer, independent resource, terms',
    },
    pt: {
      title: 'Aviso Legal | CSSBuy Spreadsheet Recurso Independente',
      description:
        'Leia o aviso legal do CSSBuy Spreadsheet, um recurso informativo independente não afiliado ou operado pelo CSSBuy.',
      keywords: 'CSSBuy Spreadsheet, CSSBuy, aviso legal, recurso independente, termos',
    },
    de: {
      title: 'Haftungsausschluss | CSSBuy Spreadsheet Unabhängige Ressource',
      description:
        'Lesen Sie den Haftungsausschluss für CSSBuy Spreadsheet, eine unabhängige Informationsressource, die nicht mit CSSBuy verbunden ist.',
      keywords: 'CSSBuy Spreadsheet, CSSBuy, Haftungsausschluss, unabhängige Ressource, Bedingungen',
    },
  },
  privacy: {
    en: {
      title: 'Privacy Policy | CSSBuy Spreadsheet',
      description:
        'Read the privacy policy for CSSBuy Spreadsheet, covering data collection, cookies, third-party links, and data security.',
      keywords: 'CSSBuy Spreadsheet, CSSBuy, privacy policy, data security, cookies',
    },
    pt: {
      title: 'Política de Privacidade | CSSBuy Spreadsheet',
      description:
        'Leia a política de privacidade do CSSBuy Spreadsheet, cobrindo coleta de dados, cookies, links de terceiros e segurança de dados.',
      keywords: 'CSSBuy Spreadsheet, CSSBuy, política de privacidade, segurança de dados, cookies',
    },
    de: {
      title: 'Datenschutzrichtlinie | CSSBuy Spreadsheet',
      description:
        'Lesen Sie die Datenschutzrichtlinie für CSSBuy Spreadsheet zu Datenerfassung, Cookies, Drittanbieter-Links und Datensicherheit.',
      keywords: 'CSSBuy Spreadsheet, CSSBuy, Datenschutzrichtlinie, Datensicherheit, Cookies',
    },
  },
  terms: {
    en: {
      title: 'Terms of Use | CSSBuy Spreadsheet',
      description:
        'Read the terms of use for CSSBuy Spreadsheet, an independent informational resource. Covers external links, liability, and intellectual property.',
      keywords: 'CSSBuy Spreadsheet, CSSBuy, terms of use, liability, intellectual property',
    },
    pt: {
      title: 'Termos de Uso | CSSBuy Spreadsheet',
      description:
        'Leia os termos de uso do CSSBuy Spreadsheet, um recurso informativo independente. Cobre links externos, responsabilidade e propriedade intelectual.',
      keywords: 'CSSBuy Spreadsheet, CSSBuy, termos de uso, responsabilidade, propriedade intelectual',
    },
    de: {
      title: 'Nutzungsbedingungen | CSSBuy Spreadsheet',
      description:
        'Lesen Sie die Nutzungsbedingungen für CSSBuy Spreadsheet, eine unabhängige Informationsressource. Zu externen Links, Haftung und geistigem Eigentum.',
      keywords: 'CSSBuy Spreadsheet, CSSBuy, Nutzungsbedingungen, Haftung, geistiges Eigentum',
    },
  },
};

const articleKeywords: Record<Lang, string> = {
  en: 'CSSBuy Spreadsheet, CSSBuy, buying guide, product research, QC, shipping, warehouse, review',
  pt: 'CSSBuy Spreadsheet, CSSBuy, guia de compra, pesquisa de produtos, QC, envio, armazém, review',
  de: 'CSSBuy Spreadsheet, CSSBuy, Kaufguide, Produktrecherche, QC, Versand, Lager, Review',
};

export function resolveLang(pathname: string): Lang {
  const first = pathname.split('/').filter(Boolean)[0];
  if (first === 'pt' || first === 'de' || first === 'en') return first;
  return 'en';
}

function normalizePath(pathname: string): string {
  if (!pathname || pathname === '/') return '/';
  return pathname.replace(/\/+$/, '');
}

export function resolveSeo(pathname: string): SeoMeta {
  const normalized = normalizePath(pathname);
  const parts = normalized.split('/').filter(Boolean);
  const lang = resolveLang(pathname);

  // Home (root or language home)
  if (parts.length === 0 || (parts.length === 1 && (parts[0] === 'en' || parts[0] === 'pt' || parts[0] === 'de'))) {
    const home = homeSeo[lang];
    const canonicalPath = parts.length === 0 ? '/' : `/${lang}/`;
    return {
      title: home.title,
      description: home.description,
      keywords: home.keywords,
      canonicalPath,
      image: SITE_IMAGE,
      ogType: 'website',
      lang,
    };
  }

  // Article page: /lang/blog/slug
  if (parts.length === 3 && parts[1] === 'blog') {
    const slug = parts[2];
    const article = articles.find((a) => a.slug === slug);
    if (article) {
      const content = article[lang] || article.en;
      return {
        title: content.title,
        description: content.metaDesc,
        keywords: articleKeywords[lang],
        canonicalPath: `/${lang}/blog/${slug}`,
        image: article.image,
        ogType: 'article',
        lang,
      };
    }
  }

  // Legal pages: /lang/about | /lang/disclaimer | /lang/privacy | /lang/terms
  if (parts.length === 2 && legalSeo[parts[1]]) {
    const page = legalSeo[parts[1]];
    const data = page[lang] || page.en;
    return {
      title: data.title,
      description: data.description,
      keywords: data.keywords,
      canonicalPath: `/${lang}/${parts[1]}/`,
      image: SITE_IMAGE,
      ogType: 'website',
      lang,
    };
  }

  // Fallback (unknown / 404): use home data with current path
  const home = homeSeo[lang];
  return {
    title: home.title,
    description: home.description,
    keywords: home.keywords,
    canonicalPath: normalized,
    image: SITE_IMAGE,
    ogType: 'website',
    lang,
  };
}